import { Box } from '@mui/material';
import { Header } from 'components/home';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import React, { useEffect, useState } from 'react';
import useCifaStore from 'stores/cifa/cifa.store';
import { ROUTES } from 'routes/constants';
import { useNavigate, useLocation } from 'react-router-dom';
import ViewSchoolFeeding from './ViewSchoolFeeding';

type NumericStringNull = number | string | null;

interface Produto {
  id: string;
  nomeRazao: string;
  documento: string;
  valor: string;
  valorNumerico: number;
}

interface SecaoAquisicao {
  titulo: string;
  produtosCnpj: Produto[];
  produtosCpf: Produto[];
}

interface SchoolFeedingValues {
  alimentacao_af_recurso_pnae: NumericStringNull;
  alimentacao_af_recurso_estado: NumericStringNull;
  alimentacao_af_recurso_prefeitura: NumericStringNull;
  alimentacao_total_recurso_pnae: NumericStringNull;
  alimentacao_total_recurso_estado: NumericStringNull;
  alimentacao_total_recurso_prefeitura: NumericStringNull;
  alimentacao_ata_aprovacao_uuid?: string | null;
  secoesAquisicao?: SecaoAquisicao[];
}


const ViewSchoolFeedingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cifa, getCifa } = useCifaStore();

  // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
  const match = /cifa-(\d{4})/.exec(location.pathname);
  const anoReferencia = match ? Number(match[1]) : 2026;

  const [values, setValues] = useState<SchoolFeedingValues>({
    alimentacao_af_recurso_pnae: null,
    alimentacao_af_recurso_estado: null,
    alimentacao_af_recurso_prefeitura: null,
    alimentacao_total_recurso_pnae: null,
    alimentacao_total_recurso_estado: null,
    alimentacao_total_recurso_prefeitura: null,
    alimentacao_ata_aprovacao_uuid: null,
    secoesAquisicao: [],
  });

  // Carregar dados do backend ao montar o componente
  useEffect(() => {
    const loadData = async () => {
      if (!cifa.uuid) {
        navigate(ROUTES[`CIFA${anoReferencia}` as keyof typeof ROUTES] as string);
        return;
      }

      // Se não tem dados de aquisições no store, carrega do backend
      const hasAquisicoes =
        cifa.aquisicoes_pnae_federal_detalhamento ||
        cifa.aquisicoes_governo_estado_detalhamento ||
        cifa.aquisicoes_prefeitura_detalhamento;

      if (!hasAquisicoes) {
        await getCifa(cifa.institution_uuid as string, anoReferencia);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Processar dados do store sempre que mudar
  useEffect(() => {
    if (!cifa.uuid) {
      navigate(ROUTES[`CIFA${anoReferencia}` as keyof typeof ROUTES] as string);
      return;
    }

    // Carregar dados das aquisições do backend
    const secoesAquisicao: SecaoAquisicao[] = [
      {
        titulo: 'Aquisições com Recurso PNAE Federal',
        produtosCnpj: cifa.aquisicoes_pnae_federal_detalhamento?.cnpj?.map((p: any, index: number) => ({
          id: `pnae-cnpj-${index}`,
          nomeRazao: p.nomeRazao || p.nome_razao_social || '',
          documento: p.cnpj || '',
          valor: (p.valor || 0).toString(),
          valorNumerico: Number(p.valor) || 0
        })) ?? [],
        produtosCpf: cifa.aquisicoes_pnae_federal_detalhamento?.cpf?.map((p: any, index: number) => ({
          id: `pnae-cpf-${index}`,
          nomeRazao: p.nomeRazao || p.nome_razao_social || '',
          documento: p.cpf || '',
          valor: (p.valor || 0).toString(),
          valorNumerico: Number(p.valor) || 0
        })) ?? []
      },
      {
        titulo: 'Aquisições com Recurso do Governo do Estado',
        produtosCnpj: cifa.aquisicoes_governo_estado_detalhamento?.cnpj?.map((p: any, index: number) => ({
          id: `estado-cnpj-${index}`,
          nomeRazao: p.nomeRazao || p.nome_razao_social || '',
          documento: p.cnpj || '',
          valor: (p.valor || 0).toString(),
          valorNumerico: Number(p.valor) || 0
        })) ?? [],
        produtosCpf: cifa.aquisicoes_governo_estado_detalhamento?.cpf?.map((p: any, index: number) => ({
          id: `estado-cpf-${index}`,
          nomeRazao: p.nomeRazao || p.nome_razao_social || '',
          documento: p.cpf || '',
          valor: (p.valor || 0).toString(),
          valorNumerico: Number(p.valor) || 0
        })) ?? []
      },
      {
        titulo: 'Aquisições com Recurso da Prefeitura',
        produtosCnpj: cifa.aquisicoes_prefeitura_detalhamento?.cnpj?.map((p: any, index: number) => ({
          id: `prefeitura-cnpj-${index}`,
          nomeRazao: p.nomeRazao || p.nome_razao_social || '',
          documento: p.cnpj || '',
          valor: (p.valor || 0).toString(),
          valorNumerico: Number(p.valor) || 0
        })) ?? [],
        produtosCpf: cifa.aquisicoes_prefeitura_detalhamento?.cpf?.map((p: any, index: number) => ({
          id: `prefeitura-cpf-${index}`,
          nomeRazao: p.nomeRazao || p.nome_razao_social || '',
          documento: p.cpf || '',
          valor: (p.valor || 0).toString(),
          valorNumerico: Number(p.valor) || 0
        })) ?? []
      }
    ];

    const newValues = {
      alimentacao_af_recurso_pnae: cifa.alimentacao_af_recurso_pnae ?? null,
      alimentacao_af_recurso_estado: cifa.alimentacao_af_recurso_estado ?? null,
      alimentacao_af_recurso_prefeitura: cifa.alimentacao_af_recurso_prefeitura ?? null,
      alimentacao_total_recurso_pnae: cifa.alimentacao_total_recurso_pnae ?? null,
      alimentacao_total_recurso_estado: cifa.alimentacao_total_recurso_estado ?? null,
      alimentacao_total_recurso_prefeitura: cifa.alimentacao_total_recurso_prefeitura ?? null,
      alimentacao_ata_aprovacao_uuid: cifa.alimentacao_ata_aprovacao_uuid ?? null,
      secoesAquisicao: secoesAquisicao,
    };

    setValues(newValues);
  }, [
    cifa.uuid,
    cifa.aquisicoes_pnae_federal_detalhamento,
    cifa.aquisicoes_governo_estado_detalhamento,
    cifa.aquisicoes_prefeitura_detalhamento,
    cifa.alimentacao_af_recurso_pnae,
    cifa.alimentacao_af_recurso_estado,
    cifa.alimentacao_af_recurso_prefeitura,
    cifa.alimentacao_total_recurso_pnae,
    cifa.alimentacao_total_recurso_estado,
    cifa.alimentacao_total_recurso_prefeitura,
    cifa.alimentacao_ata_aprovacao_uuid,
    navigate,
    anoReferencia
  ]); return (
    <MenuProvider>
      <TopNavBar />
      <Header title={`CIFA - Visualização da Alimentação Escolar - ${anoReferencia}`} showBackButton={true} />
      <Box width='97%' display='flex' flexDirection='column' justifyContent='center' alignItems='center' margin='2rem'>
        <ViewSchoolFeeding values={values} />
      </Box>
    </MenuProvider>
  );
};

export default ViewSchoolFeedingPage;
